import React, {lazy, Suspense} from 'react';
import {Route, Routes, Navigate} from "react-router-dom";
import theme from "./assets/theme";

// App CSS
import "./App.css";

// Font CSS
import "./assets/css/nucleo-svg.css";
import "./assets/css/nucleo-icons.css";

// Material import
import {ThemeProvider} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

// Toast notifications
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Routing
import routes from "./routing/routing";

// Auth Context
import { AuthProvider } from './context/AuthContext';

// Protected Route
import ProtectedRoute from './components/ProtectedRoute';

// Direct imports for auth pages
import Login from "./screens/Auth/Login";
import Register from "./screens/Auth/Register";
import AuthLayout from "./layouts/AuthLayout";
import ForgetPassword from "./screens/Auth/ForgetPassword"
// Custom components
import Loader from './components/Loader';
import VerifyEmailForm from './screens/Auth/VerifyEmaail';
import RegisterForm from './screens/Auth/RegisterNew';
import { ProfileProvider } from './context/ProfileContext';
import { isInstalledApp } from './utils/pwa';
import { InstallBanner } from './components/InstallApp';

// layout
const AppRoute = lazy(() => import("./layouts/AppLayout"));

// Lazy loaded pages
const Home = lazy(() => import("./screens/Home"));
// The installed app (opened from the phone's home screen) shows only this.
const Shipments = lazy(() => import("./screens/Shipments"));
const Logout = lazy(() => import("./screens/Auth/Logout"));

// Landing (public marketing site) — merged from the standalone Next.js app
const LandingShell = lazy(() => import("./landing/LandingShell"));
const LHome = lazy(() => import("./landing/pages/home/page"));
const LBlogs = lazy(() => import("./landing/pages/blogs/page"));
const LFAQs = lazy(() => import("./landing/pages/FAQs/page"));
const LPolicy = lazy(() => import("./landing/pages/policy/page"));

// Profile Context

function App() {
    // Read once: an installed app stays the installed app for the whole session.
    const installedApp = isInstalledApp();
    const getRoutes = (allRoutes) =>
        allRoutes.map((route) => {
            if (route.collapse) {
                return getRoutes(route.collapse);
            }

            if (route.route) {
                return <Route path={route.route} element={route.component} key={route.key}/>;
            }
            return null;
        });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <ProfileProvider>
                <AuthProvider>
                    {/* Invites the merchant to install the app; never shown inside the installed app. */}
                    {!installedApp && <InstallBanner />}
                    <Suspense fallback={<Loader />}>
                        {installedApp ? (
                            // Installed app: sign in, then the orders-and-shipping screen.
                            // Nothing else of the dashboard is reachable here.
                            <Routes>
                                <Route element={<AuthLayout />}>
                                    <Route path="login" element={<Login />} />
                                    <Route path="logout" element={<Logout />} />
                                    <Route path="forgot-password" element={<ForgetPassword />} />
                                    <Route path="verifyEmail" element={<VerifyEmailForm />} />
                                </Route>
                                <Route element={<ProtectedRoute />}>
                                    <Route path="/app/shipments" element={<Shipments />} />
                                </Route>
                                {/* Everything else (including the page the login sends to) lands on the one screen. */}
                                <Route path="*" element={<Navigate to="/app/shipments" replace />} />
                            </Routes>
                        ) : (
                            <Routes>
                                {/* Public landing site (merchant.styola.com/) */}
                                <Route element={<LandingShell />}>
                                    <Route index element={<LHome />} />
                                    <Route path="home" element={<LHome />} />
                                    <Route path="blogs" element={<LBlogs />} />
                                    <Route path="FAQs" element={<LFAQs />} />
                                    <Route path="policy" element={<LPolicy />} />
                                </Route>

                                {/* Auth Routes */}
                                <Route element={<AuthLayout />}>
                                    <Route path="login" element={<Login />} />
                                    <Route path="register"  element={<RegisterForm />}  />
                                    <Route path="logout" element={<Logout />} />
                                    <Route path="forgot-password" element={<ForgetPassword />} />
                                    <Route path="verifyEmail" element={<VerifyEmailForm />} />
                                </Route>

                                {/* App Routes - Protected */}
                                <Route element={<ProtectedRoute />}>
                                    <Route path="/app" element={<AppRoute/>}>
                                        <Route path="home" element={<Home/>} />
                                        {getRoutes(routes)}
                                    </Route>
                                    {/* Reached from a shipment notification opened in a browser tab. Not in the menu. */}
                                    <Route path="/app/shipments" element={<Shipments />} />
                                </Route>

                                {/* Unknown -> landing home */}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        )}
                    </Suspense>
                </AuthProvider>
            </ProfileProvider>
        </ThemeProvider>
    );
}

export default App;

